import { memo, useEffect } from 'react'
import * as THREE from 'three'
import { useThree } from '@react-three/fiber'
import { setOutlineResolution } from '../render/materials'
import { RenderStyleProvider } from '../render/style'
import { useDesign } from '../state/designStore'
import { useEditor } from '../state/editorStore'
import { CameraRig } from './CameraRig'
import { Interaction } from './Interaction'
import { PlacedItem, PlacementGhost } from './PlacedItems'
import { Room } from './Room'
import { setSceneHandle } from './sceneHandle'

const MemoPlacedItem = memo(PlacedItem)

/**
 * Publishes the renderer handle, keeps the contour materials sized to this
 * canvas, and mirrors the document background onto the scene.
 */
function HandleBridge() {
  const gl = useThree((state) => state.gl)
  const scene = useThree((state) => state.scene)
  const camera = useThree((state) => state.camera)
  const invalidate = useThree((state) => state.invalidate)
  const width = useThree((state) => state.size.width)
  const height = useThree((state) => state.size.height)
  const background = useDesign((state) => state.doc.style.background)
  const theme = useDesign((state) => state.doc.style.theme)
  const accent = useDesign((state) => state.doc.style.accent)
  const outlineWidth = useDesign((state) => state.doc.style.outlineWidth)
  const showOutlines = useDesign((state) => state.doc.style.showOutlines)

  useEffect(() => {
    setSceneHandle({
      gl,
      scene,
      camera: camera as THREE.OrthographicCamera,
      aspect: height > 0 ? width / height : 1,
    })
    return () => setSceneHandle(null)
  }, [gl, scene, camera, width, height])

  useEffect(() => {
    setOutlineResolution(width, height)
  }, [width, height])

  useEffect(() => {
    const colour = new THREE.Color(background)
    scene.background = colour
    gl.setClearColor(colour, 1)
    // frameloop="demand" — theme/palette/outline swaps must request a frame.
    invalidate()
  }, [scene, gl, background, theme, accent, outlineWidth, showOutlines, invalidate])

  return null
}

export function SceneRoot() {
  const doc = useDesign((state) => state.doc)
  const viewIndex = useEditor((state) => state.view.viewIndex)
  const selectedId = useEditor((state) => state.selectedId)
  const hoveredId = useEditor((state) => state.hoveredId)
  const ghost = useEditor((state) => state.ghost)
  const tool = useEditor((state) => state.tool)
  const dragPose = useEditor((state) => state.dragPose)

  return (
    <RenderStyleProvider
      theme={doc.style.theme}
      accent={doc.style.accent}
      outlineWidth={doc.style.outlineWidth}
      showOutlines={doc.style.showOutlines}
    >
      <HandleBridge />
      <CameraRig />
      <Interaction />

      <Room room={doc.room} viewIndex={viewIndex} />

      {doc.items.map((item) => {
        // The dragged item is posed from the ephemeral store, so a 60 fps drag
        // never touches the document (and never floods the undo history).
        const posed =
          dragPose && dragPose.uid === item.uid
            ? {
                ...item,
                x: dragPose.x,
                z: dragPose.z,
                y: dragPose.y,
                rotation: dragPose.rotation,
                wall: dragPose.wall,
              }
            : item
        return (
          <MemoPlacedItem
            key={item.uid}
            item={posed}
            room={doc.room}
            accent={doc.style.accent}
            selected={selectedId === item.uid}
            hovered={hoveredId === item.uid && selectedId !== item.uid}
          />
        )
      })}

      {tool.kind === 'place' && ghost ? (
        <PlacementGhost
          assetId={tool.assetId}
          variant={tool.variant}
          rotation={ghost.rotation}
          x={ghost.x}
          z={ghost.z}
          y={ghost.y}
          wall={ghost.wall}
          valid={ghost.valid}
          room={doc.room}
        />
      ) : null}
    </RenderStyleProvider>
  )
}
